import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import WebhookEndpointStatus from "enums/WebhookEndpointStatus";
import {
  CREATE_WEBHOOK_ENDPOINT,
  DELETE_WEBHOOK_ENDPOINT,
  GET_WEBHOOK_ENDPOINTS,
  GET_WEBHOOK_EVENT_TYPES,
  ROTATE_WEBHOOK_SECRET,
  UPDATE_WEBHOOK_ENDPOINT,
} from "graphql/webhookEndpoints";

export interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  status: WebhookEndpointStatus;
  revision: number;
  createdAt: number;
  updatedAt: number;
}

export interface WebhookEndpointWithSecret {
  webhookEndpoint: WebhookEndpoint;
  secret: string;
}

export interface CreateWebhookEndpointInput {
  url: string;
  events: string[];
}

export interface UpdateWebhookEndpointInput {
  url?: string;
  events?: string[];
  status?: WebhookEndpointStatus.ACTIVE | WebhookEndpointStatus.PAUSED;
  revision?: number;
}

// Create and rotate resolve with the plaintext secret, which is never readable again.
export function useWebhookEndpoints({ skip = false } = {}) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useQuery<{
    webhookEndpoints: WebhookEndpoint[];
  }>(GET_WEBHOOK_ENDPOINTS, { skip, fetchPolicy: "cache-and-network" });
  const { data: eventTypeData, loading: eventTypesLoading } = useQuery<{
    webhookEventTypes: string[];
  }>(GET_WEBHOOK_EVENT_TYPES, { skip });

  const [createMutation] = useMutation(CREATE_WEBHOOK_ENDPOINT);
  const [updateMutation] = useMutation(UPDATE_WEBHOOK_ENDPOINT);
  const [deleteMutation] = useMutation(DELETE_WEBHOOK_ENDPOINT);
  const [rotateMutation] = useMutation(ROTATE_WEBHOOK_SECRET);

  // Never rejects, or a create would lose its only copy of the secret.
  const refresh = async () => {
    try {
      await refetch();
    } catch {
      toast.error(t("WebhookListRefreshFailed"));
    }
  };

  const createWebhookEndpoint = async (
    input: CreateWebhookEndpointInput,
  ): Promise<WebhookEndpointWithSecret> => {
    const result = await createMutation({ variables: { input } });
    await refresh();
    return result.data.createWebhookEndpoint;
  };

  const updateWebhookEndpoint = async (
    id: string,
    input: UpdateWebhookEndpointInput,
  ): Promise<WebhookEndpoint> => {
    const result = await updateMutation({ variables: { id, input } });
    return result.data.updateWebhookEndpoint;
  };

  const deleteWebhookEndpoint = async (id: string): Promise<void> => {
    await deleteMutation({ variables: { id } });
    await refresh();
  };

  const rotateWebhookSecret = async (
    id: string,
  ): Promise<WebhookEndpointWithSecret> => {
    const result = await rotateMutation({ variables: { id } });
    return result.data.rotateWebhookSecret;
  };

  return {
    endpoints: data?.webhookEndpoints ?? [],
    eventTypes: eventTypeData?.webhookEventTypes ?? [],
    loading: loading || eventTypesLoading,
    error,
    createWebhookEndpoint,
    updateWebhookEndpoint,
    deleteWebhookEndpoint,
    rotateWebhookSecret,
  };
}
